import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Link,
} from '@mui/material';
import { INTEGRATIONS_API_BASE } from 'application/config/apiConfig';
import { API, graphqlOperation } from 'aws-amplify';
import type { GraphQLResult } from '@aws-amplify/api-graphql';
import {
  listStages,
  listStageCategorys,
} from '../../../../../../graphql/queries';
import { ConfirmationModal } from './ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export const LeapSetup: React.FC = () => {
  const [apiKey, setApiKey] = React.useState('');
  const [isFetching, setIsFetching] = React.useState(false);
  const [leapStages, setLeapStages] = React.useState<
    Array<{ id: string; code?: string; name: string; description?: string }>
  >([]);
  const [paivStages, setPaivStages] = React.useState<
    Array<{
      id: string;
      title: string;
      description?: string;
      sequence?: number;
    }>
  >([]);
  const [mappings, setMappings] = React.useState<
    Array<{
      leapStage: string;
      leapStageId: string;
      paivStage: string;
      paivStageId: string;
    }>
  >([]);
  const [error, setError] = React.useState<string | null>(null);
  const [saveMsg, setSaveMsg] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(true);
  const [contactsCategoryID, setContactsCategoryID] = React.useState<
    string | null
  >(null);
  const [webhookUrl, setWebhookUrl] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [existingIntegrationId, setExistingIntegrationId] = React.useState<
    string | null
  >(null);
  const [isActive, setIsActive] = React.useState<boolean | null>(null);
  const [isDisableOpen, setIsDisableOpen] = React.useState(false);
  const [isToggling, setIsToggling] = React.useState(false);
  const [loadedApiKey, setLoadedApiKey] = React.useState<string>('');
  const [loadedLeapStages, setLoadedLeapStages] = React.useState<
    Array<{ id: string; code?: string; name: string; description?: string }>
  >([]);
  const [loadedMappings, setLoadedMappings] = React.useState<
    Array<{
      leapStage: string;
      leapStageId: string;
      paivStage: string;
      paivStageId: string;
    }>
  >([]);
  const [webhookDialogOpen, setWebhookDialogOpen] = React.useState(false);
  const navigate = useNavigate();
  const [isRemoveOpen, setIsRemoveOpen] = React.useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = React.useState(false);

  const listIntegrationConfigsQuery = /* GraphQL */ `
    query ListIntegrationConfigs(
      $filter: ModelIntegrationConfigFilterInput
      $limit: Int
      $nextToken: String
    ) {
      listIntegrationConfigs(
        filter: $filter
        limit: $limit
        nextToken: $nextToken
      ) {
        items {
          id
          orgID
          crmName
          isActive
          isDeleted
          apiKey
          categoryID
          stageMapping
          createdAt
        }
        nextToken
      }
    }
  `;

  // Load Contacts category and Paiv stages
  useEffect(() => {
    (async () => {
      try {
        const orgID = (window as any)?.me?.orgID;
        if (!orgID) return;
        try {
          const catsRes = (await API.graphql(
            graphqlOperation(listStageCategorys as any, {
              filter: { orgID: { eq: orgID }, isDeleted: { eq: false } },
              limit: 1000,
            })
          )) as any;
          const allCats = (
            catsRes?.data?.listStageCategorys?.items || []
          ).filter(Boolean);
          const contacts =
            allCats.find(
              (c: any) =>
                String(c?.title || '')
                  .trim()
                  .toLowerCase() === 'contacts'
            ) ||
            allCats.find((c: any) =>
              String(c?.title || '')
                .toLowerCase()
                .includes('contact')
            ) ||
            null;
          setContactsCategoryID(contacts?.id || null);
        } catch {}

        let nextToken: string | null = null;
        let all: Array<{
          id: string;
          title: string;
          description?: string;
          sequence?: number;
        }> = [];
        do {
          const res = (await API.graphql(
            graphqlOperation(listStages as any, {
              filter: {
                orgID: { eq: orgID },
                isDeleted: { eq: false },
                isActive: { eq: true },
              },
              limit: 1000,
              nextToken,
            })
          )) as GraphQLResult<{
            listStages: { items: any[]; nextToken: string | null };
          }>;
          const items = (res.data?.listStages.items || []).map((s: any) => ({
            id: s.id,
            title: s.title,
            description: s.description,
            sequence: s.sequence,
          }));
          all = all.concat(items);
          nextToken = res.data?.listStages.nextToken || null;
        } while (nextToken);
        all.sort((a, b) => {
          const as =
            typeof a.sequence === 'number'
              ? a.sequence
              : Number.MAX_SAFE_INTEGER;
          const bs =
            typeof b.sequence === 'number'
              ? b.sequence
              : Number.MAX_SAFE_INTEGER;
          if (as !== bs) return as - bs;
          return String(a.title).localeCompare(String(b.title));
        });
        setPaivStages(all);
      } catch {}
    })();
  }, []);

  // Load existing Leap config (prefer ACTIVE; else fall back to most-recent INACTIVE so Manage can edit)
  useEffect(() => {
    (async () => {
      try {
        const orgID = (window as any)?.me?.orgID;
        if (!orgID) return;
        const res = (await API.graphql(
          graphqlOperation(listIntegrationConfigsQuery as any, {
            filter: {
              orgID: { eq: orgID },
              crmName: { eq: 'leap' },
              isDeleted: { ne: true },
            },
            limit: 50,
          })
        )) as any;
        const rawItems = (
          res?.data?.listIntegrationConfigs?.items || []
        ).filter(Boolean);
        console.log('[LeapSetup] raw listIntegrationConfigs items:', rawItems);
        const sorted = rawItems.sort((a: any, b: any) =>
          String(b?.createdAt || '').localeCompare(String(a?.createdAt || ''))
        );
        const activeOnly = sorted.filter((it: any) => it?.isActive === true);
        const picked = activeOnly[0] || sorted[0];
        console.log('[LeapSetup] picked integration:', picked);
        if (!picked) {
          setExistingIntegrationId(null);
          setIsActive(null);
          setWebhookUrl(null);
          setApiKey('');
          setLoadedApiKey('');
          setLeapStages([]);
          setLoadedLeapStages([]);
          setMappings([]);
          setLoadedMappings([]);
          setIsEditing(true);
          return;
        }
        setExistingIntegrationId(picked?.id || null);
        setIsActive(Boolean(picked?.isActive));
        setApiKey(picked?.apiKey || '');
        setLoadedApiKey(picked?.apiKey || '');

        let mappingObj: Record<string, any> = {};
        try {
          const raw = picked?.stageMapping;
          mappingObj = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
        } catch {
          mappingObj = {};
        }
        const entries: Array<{
          name: string;
          leapStageId: string;
          paivStageId: string;
          position: number;
        }> = Object.keys(mappingObj || {}).map((name) => ({
          name,
          leapStageId: String(mappingObj?.[name]?.leapStageId || ''),
          paivStageId: String(mappingObj?.[name]?.paivStageId || ''),
          position:
            typeof mappingObj?.[name]?.position === 'number'
              ? mappingObj[name].position
              : Number.MAX_SAFE_INTEGER,
        }));
        entries.sort(
          (a, b) => a.position - b.position || a.name.localeCompare(b.name)
        );

        const leapList = entries.map((e) => ({
          id: e.leapStageId || e.name,
          name: e.name,
        })) as any;
        setLeapStages(leapList);
        setLoadedLeapStages(leapList);
        const mapList = entries.map((e) => ({
          leapStage: e.name,
          paivStage: '',
          paivStageId: e.paivStageId,
          leapStageId: e.leapStageId,
        })) as any;
        setMappings(mapList);
        setLoadedMappings(mapList);

        const derivedWebhook = `${INTEGRATIONS_API_BASE}/webhooks/leap/${picked?.id}`;
        setWebhookUrl(derivedWebhook);
        setIsEditing(false);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!isEditing && paivStages.length > 0) {
      setMappings((prev) => {
        const next = prev.map((m) => {
          if (m.paivStage) return m;
          const found = paivStages.find(
            (p) => String(p.id) === String(m.paivStageId)
          );
          if (found && found.title) {
            return { ...m, paivStage: String(found.title).trim() };
          }
          return m;
        });
        return next;
      });
    }
  }, [paivStages, isEditing]);

  const fetchStages = async () => {
    if (!apiKey) return;
    setIsFetching(true);
    setError(null);
    setSaveMsg(null);
    try {
      const res = await fetch(`${INTEGRATIONS_API_BASE}/leap/stages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: (window as any)?.me?.accessToken
            ? `Bearer ${(window as any).me.accessToken}`
            : '',
        },
        body: JSON.stringify({ apiKey }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to fetch stages');
      const list = (data?.stages || data?.data || data || []).map(
        (s: any, idx: number) => ({
          id: String(s?.id ?? s?.code ?? idx),
          code: String(s?.code || ''),
          name: String(s?.name || s?.title || ''),
          description: String(s?.description || ''),
        })
      );
      setLeapStages(list);
      setMappings(
        list.map((s: { id: string; code?: string; name: string }) => ({
          leapStage: s.name,
          leapStageId: String(s?.code || s.id),
          paivStage: '',
          paivStageId: '',
        }))
      );
    } catch (e: any) {
      setError(e?.message || 'Unexpected error');
      setLeapStages([]);
      setMappings([]);
    } finally {
      setIsFetching(false);
    }
  };

  const updateMapping = (leapStage: string, paivStageTitle: string) => {
    const found = paivStages.find(
      (p) => String(p.title).trim() === paivStageTitle
    );
    setMappings((prev) => {
      const next = prev.slice();
      const idx = next.findIndex((m) => m.leapStage === leapStage);
      const existing = idx >= 0 ? next[idx] : undefined;
      const fromList = leapStages.find((ls) => ls.name === leapStage);
      const newEntry = {
        leapStage,
        leapStageId:
          existing?.leapStageId || String(fromList?.code || fromList?.id || ''),
        paivStage: paivStageTitle,
        paivStageId: found?.id || '',
      };
      if (idx >= 0) {
        next[idx] = newEntry;
      } else {
        next.push(newEntry);
      }
      return next;
    });
  };

  const allMapped = React.useMemo(() => {
    if (leapStages.length === 0) return false;
    return leapStages.every((ls) => {
      const m = mappings.find((mm) => mm.leapStage === ls.name);
      return Boolean(m && m.paivStageId);
    });
  }, [leapStages, mappings]);

  const saveIntegration = async () => {
    setIsSaving(true);
    setSaveMsg(null);
    setError(null);
    setCopied(false);
    try {
      if (!allMapped) {
        setError('Please map every Leap stage to a Paiv stage before saving.');
        return;
      }
      if (!contactsCategoryID) {
        setError(
          'Contacts category not found for your org. Please create a "Contacts" stage category and try again.'
        );
        return;
      }
      const orgID = (window as any)?.me?.orgID;
      const userId = (window as any)?.me?.userID;
      const payload = {
        id: existingIntegrationId || undefined,
        orgID,
        userId,
        crmName: 'leap',
        locations: [
          {
            apiKey,
            categoryID: contactsCategoryID || undefined,
            stageMappings: mappings,
          },
        ],
      };
      const res = await fetch(
        `${INTEGRATIONS_API_BASE}/leap/setup-leap-integration`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: (window as any)?.me?.accessToken
              ? `Bearer ${(window as any).me.accessToken}`
              : '',
          },
          body: JSON.stringify(payload),
        }
      );
      const j = await res.json();
      if (!res.ok || j?.success === false)
        throw new Error(j?.error || 'Failed to save integration');
      const url =
        j?.webhookUrl || j?.item?.webhookUrl || j?.data?.webhookUrl || null;
      setWebhookUrl(url);
      setSaveMsg('Leap integration saved');
      setIsEditing(false);
      if (!existingIntegrationId && (j?.id || j?.item?.id)) {
        setExistingIntegrationId(j?.id || j?.item?.id);
      }
      setIsActive(true);
      setLoadedApiKey(apiKey);
      setLoadedLeapStages(leapStages);
      setLoadedMappings(mappings);
      if (url) setWebhookDialogOpen(true);
    } catch (e: any) {
      setError(e?.message || 'Unexpected error saving');
    } finally {
      setIsSaving(false);
    }
  };

  const copyWebhook = async () => {
    if (!webhookUrl) return;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };

  const handleToggleActive = async (nextActive: boolean) => {
    try {
      if (!existingIntegrationId) return;
      if (!nextActive) {
        setIsDisableOpen(true);
        return;
      }
      setError(null);
      setSaveMsg(null);
      setIsToggling(true);
      const payload = {
        id: existingIntegrationId,
        isActive: nextActive,
      } as any;
      const url = `${INTEGRATIONS_API_BASE}/leap/setup-leap-integration`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: (window as any)?.me?.accessToken
            ? `Bearer ${(window as any).me?.accessToken}`
            : '',
        },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.success === false)
        throw new Error(j?.error || 'Failed to toggle integration');
      setIsActive(nextActive);
      setSaveMsg('Integration enabled');
    } catch (e: any) {
      setError(e?.message || 'Unexpected error while toggling');
    } finally {
      setIsToggling(false);
    }
  };

  const confirmDisable = async () => {
    try {
      setIsDisableOpen(false);
      if (!existingIntegrationId) return;
      setError(null);
      setSaveMsg(null);
      setIsToggling(true);
      const payload = { id: existingIntegrationId, isActive: false } as any;
      const url = `${INTEGRATIONS_API_BASE}/leap/setup-leap-integration`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: (window as any)?.me?.accessToken
            ? `Bearer ${(window as any).me?.accessToken}`
            : '',
        },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.success === false)
        throw new Error(j?.error || 'Failed to disable integration');
      setIsActive(false);
      setSaveMsg(
        'Integration disabled. Please remove the webhook from Leap to stop events.'
      );
    } catch (e: any) {
      setError(e?.message || 'Unexpected error while disabling');
    } finally {
      setIsToggling(false);
    }
  };

  const confirmRemove = async () => {
    try {
      setIsRemoveOpen(false);
      if (!existingIntegrationId) return;
      setError(null);
      setSaveMsg(null);
      setIsToggling(true);
      const payload = {
        id: existingIntegrationId,
        isActive: false,
        isDeleted: true,
      } as any;
      const url = `${INTEGRATIONS_API_BASE}/leap/setup-leap-integration`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: (window as any)?.me?.accessToken
            ? `Bearer ${(window as any).me?.accessToken}`
            : '',
        },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.success === false)
        throw new Error(j?.error || 'Failed to remove integration');
      setIsActive(false);
      setSaveMsg(
        'Integration removed. Please remove the webhook from Leap to stop events.'
      );
      // Clear loaded fields so UI does not repopulate
      setExistingIntegrationId(null);
      setWebhookUrl(null);
      setApiKey('');
      setLeapStages([]);
      setMappings([]);
      setIsEditing(true);
    } catch (e: any) {
      setError(e?.message || 'Unexpected error while removing');
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <Box sx={{ p: 3, mt: 10, width: '100%' }}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Leap Integration Setup
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="text" onClick={() => setHelpDialogOpen(true)}>
              Help
            </Button>
            {existingIntegrationId && !isEditing && (
              <Button
                variant="outlined"
                color={isActive ? ('warning' as any) : ('success' as any)}
                onClick={() => handleToggleActive(!isActive)}
                disabled={isToggling}
              >
                {isActive
                  ? isToggling
                    ? 'Disabling…'
                    : 'Disable Integration'
                  : isToggling
                  ? 'Enabling…'
                  : 'Enable Integration'}
              </Button>
            )}
            {existingIntegrationId && isEditing && (
              <Button
                variant="text"
                color="error"
                onClick={() => {
                  setIsRemoveOpen(true);
                }}
              >
                Remove Integration
              </Button>
            )}
            {!isEditing && (
              <Button variant="contained" onClick={() => setIsEditing(true)}>
                Edit Configuration
              </Button>
            )}
          </Box>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {saveMsg && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {saveMsg}
          </Alert>
        )}

        {webhookUrl && (
          <Box
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Webhook URL
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                value={webhookUrl || ''}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <Button variant="outlined" size="small" onClick={copyWebhook}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Add this URL in Leap so we can receive webhooks.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">API Key</Typography>
            <Tooltip
              title="Generate this token in Leap: Settings → Developer → Generate Token. Click to view help."
              arrow
            >
              <IconButton
                size="small"
                sx={{ ml: 1 }}
                onClick={() => setHelpDialogOpen(true)}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Leap API Key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              sx={{ width: 420 }}
              size="small"
              disabled={!isEditing}
            />
            <Button
              variant="contained"
              onClick={fetchStages}
              disabled={!apiKey || isFetching || !isEditing}
            >
              {isFetching ? 'Fetching…' : 'Fetch Stages'}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Stage Mapping
          </Typography>
          {leapStages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No Leap stages loaded yet. Enter your API key and click Fetch
              Stages.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {leapStages.map((ls, idx) => {
                const current = mappings.find((m) => m.leapStage === ls.name);
                const value = current?.paivStage || '';
                return (
                  <React.Fragment key={ls.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1,
                        backgroundColor:
                          idx % 2 === 1 ? 'background.default' : 'transparent',
                      }}
                    >
                      <Box sx={{ width: 260 }}>
                        <Typography variant="subtitle1">{ls.name}</Typography>
                      </Box>
                      <FormControl sx={{ minWidth: 320 }} size="small">
                        <Select
                          value={value}
                          displayEmpty
                          renderValue={(selected) => {
                            const v = String(selected || '').trim();
                            return v || 'Paiv Stage';
                          }}
                          onChange={(e) =>
                            updateMapping(ls.name, String(e.target.value))
                          }
                          sx={{
                            backgroundColor: 'background.paper',
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'background.paper',
                            },
                            '& .MuiSelect-select': {
                              backgroundColor: 'background.paper',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              elevation: 8,
                              sx: { backgroundColor: 'background.paper' },
                            },
                          }}
                          disabled={!isEditing}
                        >
                          {paivStages.map((ps) => (
                            <MenuItem key={ps.id} value={ps.title}>
                              {ps.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {idx < leapStages.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setApiKey(loadedApiKey);
              setLeapStages(loadedLeapStages);
              setMappings(loadedMappings);
              setError(null);
              setSaveMsg(null);
              setIsEditing(false);
            }}
            disabled={!isEditing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={saveIntegration}
            disabled={
              isSaving ||
              !apiKey ||
              mappings.length === 0 ||
              !allMapped ||
              !isEditing
            }
          >
            {isSaving ? 'Saving…' : 'Save Integration'}
          </Button>
        </Box>
      </Box>

      <ConfirmationModal
        open={isDisableOpen}
        title="Disable Leap Integration"
        message="Are you sure you want to disable the Leap integration? This will stop syncing. Please also remove the webhook from Leap to stop events."
        confirmText="Disable"
        onConfirm={confirmDisable}
        onCancel={() => setIsDisableOpen(false)}
        severity="warning"
      />

      <ConfirmationModal
        open={isRemoveOpen}
        title="Remove Leap Integration"
        message="This will permanently remove the Leap integration configuration. You can re-connect later, but current mappings and the API key will be cleared. Continue?"
        confirmText="Remove"
        onConfirm={confirmRemove}
        onCancel={() => setIsRemoveOpen(false)}
        severity="error"
      />

      <Dialog
        open={webhookDialogOpen}
        onClose={() => setWebhookDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Webhook URL</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Copy this URL into Leap to enable webhooks:
          </Typography>
          <TextField
            value={webhookUrl || ''}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setWebhookDialogOpen(false)}
            color="inherit"
            variant="outlined"
          >
            Close
          </Button>
          <Button onClick={copyWebhook} variant="contained">
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{ '& .MuiDialog-paper': { maxHeight: '70vh' } }}
      >
        <DialogTitle>Leap Integration Help</DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ mb: 2 }}>
            1. Get your Leap API Key
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Sign in to your Leap account as an administrator." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Navigate to Settings → Developer → Generate and Copy a Token (Name this token 'Paiv Integration')." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Paste the Token into the 'Leap API Key' field on the Paiv Leap setup page and click 'Fetch Stages'. Match your Leap stages with Paiv stages." />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            2. Map Leap Stages to Paiv Stages
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="On the Paiv Leap setup page, select the corresponding Paiv stage for each Leap workflow stage." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Click 'Save Integration'. All mappings and your API key will be securely stored." />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            3. Add the Paiv Webhook in Leap
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Save your integration in Paiv first. After saving, Paiv will show your unique Webhook URL. Copy this URL." />
            </ListItem>
            <ListItem>
              <ListItemText primary="In Leap, go to Settings → Webhooks → Add a new Webhook URL → Save" />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Troubleshooting
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Failed to Fetch Stages"
                secondary="Verify the API Key is valid and has access; if you can fetch your stages, then the API key is valid."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Webhook Not Firing"
                secondary="Confirm the webhook is enabled in Leap and points to the exact Paiv URL shown after saving the integration."
              />
            </ListItem>
          </List>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Need assistance? Contact{' '}
            <Link href="mailto:support@paiv.ai">support@paiv.ai</Link>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeapSetup;
